import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { EmailLinksEditor } from 'components/EmailLinksEditor'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { submitNote } from 'services/apis/conditions'
import { svgLoading } from 'stories/assets'
import { Button } from 'stories/components'
import { changeObject2Array, getPartiesMap, InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { noteInputs } from '../constant'
import {
  availabilityPayload,
  availabilityValues,
  useTemplateAvailabilityOptions,
} from '../useTemplateAvailability'
import { type INoteButton, type INoteTemplate } from './types'

export function NoteDetails(props: {
  defaults: INoteTemplate | null
  lastUpdatedAt: number
  onBack: Function
  onComplete: Function
  /**
   * Who a NEW template starts out available to. The picker passes the person adding it, so a
   * template created mid-conversation is theirs rather than another company-wide entry in
   * everyone's list. They can widen it in the same form before saving.
   */
  defaultAvailableUserIds?: number[]
}) {
  const [isLoading, setLoading] = useState(false)
  const [inputs, setInputs] = useState<Record<string, any>>({})
  const [links, setLinks] = useState<INoteButton[]>([])
  const [showError, setShowError] = useState(false)
  const [cursorPos, setCursorPos] = useState(0)
  const [focusField, setFocusField] = useState<string>('')
  const { defaults = null, lastUpdatedAt, defaultAvailableUserIds } = props
  const { profileOptions, userOptions, teamOptions } = useTemplateAvailabilityOptions()

  const activeInputRef = useRef(null)

  useEffect(() => {
    const newStats = cloneDeep(noteInputs()) as Record<string, any>
    // Titles and tooltips come with availabilityInputs(); only the options are dynamic.
    newStats.availableAccountTypes.options = profileOptions()
    newStats.availableUserIds.options = userOptions
    newStats.availableTeamIds.options = teamOptions

    if (!defaults) {
      if (defaultAvailableUserIds?.length) {
        newStats.availableUserIds.value = defaultAvailableUserIds.map((id) => String(id))
      }
      setInputs(newStats)
      setLinks([])
      return
    }
    const availability: Record<string, any> = availabilityValues(defaults)
    for (const key in newStats) {
      newStats[key].value = availability[key] !== undefined ? availability[key] : (defaults as any)[key]
    }
    // Templates saved before the scope existed have no value: an empty list means every list.
    newStats.notePageTypes.value = defaults.notePageTypes || []
    setInputs(newStats)
    setLinks(defaults?.links || [])
  }, [defaults, lastUpdatedAt])

  // Options arrive after the form is built; inject them without touching what is typed in it.
  useEffect(() => {
    setInputs((prev) => {
      if (!prev.availableUserIds) return prev
      const next = cloneDeep(prev)
      next.availableUserIds.options = userOptions
      next.availableTeamIds.options = teamOptions
      return next
    })
  }, [userOptions, teamOptions])

  const onBack = () => {
    props.onBack()
  }

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    newInputs[key].error = ''
    setInputs(newInputs)
  }

  const handleFocus = (event: any, fieldKey: string) => {
    activeInputRef.current = event.target
    setCursorPos(event.target.selectionStart)
    setFocusField(fieldKey)
  }

  const handleCursorChange = (event: any) => {
    setCursorPos(event.target.selectionStart)
  }

  const insertTextAtCursor = (text: string) => {
    const input: any = activeInputRef.current
    if (!input) return

    const value = input.value
    const newValue = value.substring(0, cursorPos) + text + value.substring(cursorPos)
    const newCursorPos = cursorPos + text.length

    onChange(focusField, newValue)
    setTimeout(() => {
      input.focus({ preventScroll: true })
      input.setSelectionRange(newCursorPos, newCursorPos)
      setCursorPos(newCursorPos)
    }, 0)
  }

  const onSubmit = () => {
    let hasError = false
    const newStats = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in newStats) {
      const { value, disabled = false } = newStats[key]
      let error = InputValidate(newStats[key])
      newStats[key].error = error
      if (error.length > 0) hasError = true
      if (!disabled && value !== undefined) data[key] = value
    }

    links.map((item) => {
      if (!item.label || !item.url) hasError = true
    })
    data.links = links
    data.notePageTypes = changeObject2Array(data.notePageTypes)
    Object.assign(data, availabilityPayload(data, { userOptions, teamOptions }))

    if (hasError) {
      setInputs(newStats)
      setShowError(true)
      return
    }

    setLoading(true)
    const id = defaults ? defaults.id : 0
    submitNote(id, data)
      .then(() => {
        if (id == 0) toast('New note is added.', { type: 'success' })
        else toast(`Note is updated.`, { type: 'success' })
        props.onComplete()
        onBack()
      })
      .catch(() => setLoading(false))
  }

  const placeholders = getPartiesMap({})

  return (
    <div className="note-container">
      <h2 className="text-2xl font-bold flex items-center mb-3">
        Notes - {!defaults ? 'Add' : `Update`}
        {isLoading && (
          <span className="ml-3">
            <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin" />
          </span>
        )}
      </h2>
      <Button link onClick={onBack}>
        <div className="flex text-shade-blue items-center">
          <ArrowLeftIcon className="w-4 h-4 mr-1" /> <p>Return to Notes</p>
        </div>
      </Button>

      <div className="my-4">
        <div className="border-b mb-2">- Placeholders</div>
        <pre className="flex gap-1 flex-wrap">
          {Object.keys(placeholders)
            .sort()
            .map((key, index) => {
              return (
                <div
                  key={index}
                  className="mr-2 cursor-pointer hover:underline"
                  onClick={() => insertTextAtCursor(`{{${key}}}`)}
                >
                  {key},{' '}
                </div>
              )
            })}
        </pre>
      </div>

      <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-3">
        {Object.keys(inputs).map((key, index) => {
          let input = inputs[key]
          return (
            <div className={`input col-span-${input.span}`} key={index}>
              <RenderInput
                input={input}
                Key={key}
                onChange={onChange}
                onFocus={(e: any) => handleFocus(e, key)}
                onKeyUp={handleCursorChange}
                onClick={handleCursorChange}
              />
            </div>
          )
        })}
        <EmailLinksEditor value={links} onChange={setLinks} properties={{ showError }} />
      </div>
      <div className="block text-center">
        <Button onClick={onSubmit} className="px-10" loading={isLoading}>
          <>{defaults ? 'Update' : 'Add'} Note</>
        </Button>
      </div>
    </div>
  )
}
